import { FasletWidget as FasletWidgetFC, Variant } from "@faslet/react-widget"
import { ProductSizeBase } from "@jog/types"
import { ReactElement, useCallback, useMemo, useState } from "react"

import { ClientOnly } from "../../ClientOnly"
import { useI18n, usePdp, usePdpAdded } from "../../hooks"
import { CartStore } from "../../stores"

declare global {
    interface Window {
        _faslet: {
            openWidget?: () => void
        }
    }
}
function transSizeToFasletVariants(size: ProductSizeBase, price: number): Omit<Variant, "sizeLabel"> {
    return {
        variantId: size.sku,
        inStock: size.isOnStock,
        sku: size.sku,
        colorId: ``,
        price,
    }
}
export const FasletWidget = ({ cartStore, Icon }: { cartStore: CartStore; Icon: ReactElement }) => {
    // Get the product variants and colors
    const { $t } = useI18n()
    const [btnLabel, setBtnLabel] = useState("")
    const shopId = process.env.NEXT_PUBLIC_FASLET_SHOP_ID
    const onAdd = usePdpAdded()
    const { setSku, detail, productData, fasletAvailable, setFasletAvailable } = usePdp()

    const variants = useMemo(() => {
        const price = productData?.price
            ? +(productData.price.centAmount / Math.pow(10, productData.price.fractionDigits)).toFixed(2)
            : 0
        switch (productData?.sizeGroup.type) {
            case "size": {
                return productData?.sizeGroup.sizes.map((i) => ({
                    ...transSizeToFasletVariants(i, price),
                    sizeLabel: i.size,
                }))
            }
            case "waist_length": {
                return productData?.sizeGroup.sizes.map((i) => ({
                    ...transSizeToFasletVariants(i, price),
                    sizeLabel: `${i.waist}/${i.length}`,
                }))
            }
            default: {
                return []
            }
        }
    }, [productData])

    const addProductToCart = useCallback(
        async (id: string) => {
            const product = variants.find((variant) => variant.variantId === id)
            const sku = product?.sku || ""
            setSku(sku)
            await cartStore.addProductsToCart([{ sku, quantity: 1 }])
            onAdd()
        },
        [cartStore, onAdd, setSku, variants]
    )
    if (!shopId) return <></>
    if (!detail.gtmId) return <></>
    if (detail.ctpId !== productData?.id) return <></>
    return (
        <ClientOnly>
            <div id="faslet-button">
                <FasletWidgetFC
                    locale={process.env.NEXT_PUBLIC_LOCALE || "en"}
                    productId={detail.gtmId}
                    shopId={shopId}
                    brandId={productData?.brand || ""}
                    productImageUrl={detail.desktopImages?.[0]?.url || ""}
                    productName={detail.name}
                    variants={variants}
                    onAddToCart={addProductToCart}
                    onResult={({ label }) => {
                        const product = variants.find((variant) => variant.sizeLabel === label)
                        if (!product) return
                        setBtnLabel(product.sizeLabel)
                        if (product?.inStock) {
                            setSku(product.sku)
                        }
                    }}
                    onButtonShow={() => setFasletAvailable(true)}
                    onButtonHidden={() => setFasletAvailable(false)}
                />
                {/* custom button text */}
                {fasletAvailable && (
                    <button
                        type="button"
                        className="mt-4 inline-block cursor-pointer gap-1 text-sm leading-4"
                        onClick={() => window?._faslet?.openWidget?.()}>
                        {btnLabel ? (
                            <>
                                <span>{$t("Your size is")}</span>
                                <span className="ml-2 rounded border border-gray-silver px-2 pt-1.5 pb-1">
                                    {btnLabel}
                                </span>
                            </>
                        ) : (
                            <span className="flex items-center">
                                {Icon}
                                <span className="ml-1 underline">{$t("Measure indicator")}</span>
                            </span>
                        )}
                    </button>
                )}
            </div>
        </ClientOnly>
    )
}
